/** Angular `PaymentProcessAdjustmentComponent` default `riskPercentages`. */
export const DEFAULT_RISK_PERCENTAGES: Record<string, string> = {
  HIGH: '125.00',
  MEDIUM: '100.00',
  LOW: '50.00',
}

/**
 * Disable approve / access actions when application risk rating is denied.
 * Plain products: `DENIED`. Dynamic access fee products: `DENIED - 0`, etc.
 */
export function isDeniedRiskRating(riskRating: unknown): boolean {
  const normalized = String(riskRating ?? '').trim().toLowerCase()
  if (!normalized) return false
  return normalized.includes('denied')
}

export const RISK_RATING_OPTIONS = [
  { value: 'LOW', label: 'LOW' },
  { value: 'MEDIUM', label: 'MEDIUM' },
  { value: 'HIGH', label: 'HIGH' },
] as const

export function resolveRiskPercentages(raw: unknown): Record<string, string> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, string>
  }
  return DEFAULT_RISK_PERCENTAGES
}

/** Access fee from rent + rating (Angular RiskRatingPopup). */
export function calculateRiskRatingAccessFee(
  rentAmount: number,
  riskRating: string,
): number {
  const rating = riskRating || 'MEDIUM'
  switch (rating) {
    case 'LOW':
      return rentAmount * 0.5
    case 'MEDIUM':
      return rentAmount * 1.0
    case 'HIGH':
      return rentAmount * 1.25
    default:
      return 0
  }
}

/** DAF path — `rent * (riskPercentages[rating] / 100)`. */
export function calculateAccessFeeFromPercentages(
  rentAmount: number,
  riskRating: string,
  riskPercentages: Record<string, string>,
): number {
  const pct = Number(riskPercentages[riskRating] ?? riskPercentages.MEDIUM ?? 100)
  if (!Number.isFinite(pct)) {
    return calculateRiskRatingAccessFee(rentAmount, riskRating)
  }
  return rentAmount * (pct / 100)
}

/** Angular `calculateOwnPayment`: `balance_due = access_fee - premium_paid`. */
export function calculateBalanceDue(accessFee: number, premiumPaid: number): number {
  return accessFee - premiumPaid
}

export function computeRiskAdjustmentAmounts(
  applicationAllData: Record<string, unknown>,
  riskRating: string,
): { rentAmount: number; accessFee: number; balanceDue: number } {
  const rentAmount = Number(applicationAllData.rent_amount ?? 0)
  const premiumPaid = Number(applicationAllData.premium_paid ?? 0)
  const property = applicationAllData.property as { allow_dynamic_access_fee?: boolean } | undefined
  const usePercentages = Boolean(property?.allow_dynamic_access_fee)
  const riskPercentages = resolveRiskPercentages(applicationAllData.riskPercentages)

  const accessFee = usePercentages
    ? calculateAccessFeeFromPercentages(rentAmount, riskRating, riskPercentages)
    : calculateRiskRatingAccessFee(rentAmount, riskRating)

  return {
    rentAmount,
    accessFee,
    balanceDue: calculateBalanceDue(accessFee, premiumPaid),
  }
}

/** Risk popup access fee via percentages; unknown rating → fallback (Angular validateRiskRating). */
export function calculateRiskRatingPopupAccessFee(
  rentAmount: number,
  riskRating: string,
  riskPercentages: Record<string, string>,
  fallbackAccessFee: number,
): number {
  if (!riskRating || riskPercentages[riskRating] == null) {
    return fallbackAccessFee
  }
  const percentage = Number(riskPercentages[riskRating])
  if (!Number.isFinite(percentage)) {
    return fallbackAccessFee
  }
  return rentAmount * (percentage / 100)
}

/** Angular risk-rating popup summary table amounts. */
export function computeRiskRatingPopupAmounts(
  applicationAllData: Record<string, unknown>,
  riskRating: string,
): {
  rentAmount: string
  oldAccessFee: string
  newAccessFee: string
  accessFeeChanged: boolean
} {
  const rentAmount = formatPlainMoney(Number(applicationAllData.rent_amount ?? 0))
  const oldAccessFee = formatPlainMoney(Number(applicationAllData.access_fee ?? 0))
  const rentNumeric = Number(applicationAllData.rent_amount ?? 0)
  const riskPercentages = resolveRiskPercentages(applicationAllData.riskPercentages)
  const newAccessFee = formatPlainMoney(
    calculateRiskRatingPopupAccessFee(
      rentNumeric,
      riskRating,
      riskPercentages,
      Number(applicationAllData.access_fee ?? 0),
    ),
  )

  return {
    rentAmount,
    oldAccessFee,
    newAccessFee,
    accessFeeChanged: oldAccessFee !== newAccessFee,
  }
}

/** Plain `1000.00` display (Angular risk adjustment summary table). */
export function formatPlainMoney(amount: number): string {
  if (!Number.isFinite(amount)) return '0.00'
  return amount.toFixed(2)
}
